/**
 * A preparação automática do pacote do caso eletrônico (E168.F7, GUS-180, ADR-0116 §9). Espelha
 * `apps/api/types/preparacao_do_pacote.py`.
 *
 * - `GET /casos/:id/pacote`: as quatro linhas do card (Documentos e fontes, Análise, Exame,
 *   Relatório de risco), a versão da análise que as prévias compõem e se o pacote está completo.
 *   Só a equipe. A leitura também pede de novo a preparação que ficou para trás.
 * - `POST /casos/:id/pacote/retomadas`: refaz só a saída que falhou, com o motivo, em nome de quem
 *   pediu. 409 com uma preparação em andamento ou com a saída que não está para retomar.
 * - `GET /casos/:id/pacote/:saida.pdf`: a prévia guardada da versão vigente, com o código dos bytes.
 *
 * E169.F2 (GUS-155, ADR-0117 §4): assinado o laudo, o pacote é a entrega ao cliente — o exame
 * final e, em PARTE, o relatório de risco liberado da mesma versão. A liberação do risco é mais uma
 * preparação, com as próprias tentativas e "Retomar" sem assinar de novo.
 *
 * E170.F2 (GUS-229): arquivo gerado não é conferência aprovada. A saída com o arquivo guardado diz o
 * ponto da jornada — gerado, bloqueado na conferência por regras (com os critérios que não
 * passaram), aguardando a revisão do perito, revisado, assinado e entregue ao cliente.
 *
 * E172.F19 (GUS-230 item 5): enquanto a preparação renova o pacote (depois da conferência do
 * portal, da revisão ou da análise facial), a saída sem final vem em andamento, sem a prévia
 * anterior, e o download responde 409 `PREVIA_EM_ATUALIZACAO`: a tela diz "atualizando" e não serve
 * o PDF anterior como o da análise nova.
 *
 * Nada é decidido aqui: o estado, o motivo e o que retomar chegam prontos do servidor.
 */
import axios, { type AxiosInstance } from 'axios';

import type { ChaveDaConferencia } from '@/lib/conferenciaDasSaidas';
import { codigoDeVerificacao, nomeDoAnexo, type ArquivoBaixado } from '@/lib/http_download';
import { rehydrateBlobError } from '@/lib/laudo';
import type { MotivoDaProntidao, ProximoPasso } from '@/lib/resultadoAuditado';

export type SaidaDoPacote = 'exame' | 'riscos';
export type ChaveDaLinha = 'documentos' | 'analise' | 'exame' | 'riscos';
export type EstadoDaLinha =
  | 'pronto'
  | 'em_andamento'
  | 'aguardando'
  | 'bloqueado'
  | 'falhou'
  | 'nao_se_aplica';
/** E170.F2: onde a saída com o arquivo guardado está na jornada. */
export type JornadaDaSaida =
  | 'gerado'
  | 'bloqueado'
  | 'aguardando_revisao'
  | 'revisado'
  | 'assinado'
  | 'entregue';
export type ModoDoPacote = 'parte' | 'juizo';

/** Por que a saída falhou: sempre técnico, nunca ausência de documento nem mérito. */
export type MotivoTecnico =
  | 'composicao_falhou'
  | 'conversao_falhou'
  | 'gravacao_falhou'
  | 'interrompida';

/** Por que a linha aguarda, fora os motivos da prontidão do caso. */
export type MotivoDeEsperaProprio =
  | 'leitura_em_andamento'
  | 'analise_facial_em_andamento'
  | 'sem_avaliacao'
  | 'busca_nao_conferida'
  | 'sem_ator'
  /** E216.F4.C02 (GUS-317 3b): o caso de entrada sem a conclusão do cadastro. */
  | 'cadastro_nao_concluido'
  /** Owner, 04/11 (GT-2026-0076): sem perfil normativo para o ato, as prévias não são compostas. */
  | 'sem_perfil_normativo';

/** O risco depois do laudo assinado (E169.F2): a liberar, o final que a conferência recusou, ou o
 * caso sem o banco cadastrado (E170.F4.C04). */
export type MotivoDaLiberacao =
  | 'liberacao_pendente'
  | 'reservado_nao_conferido'
  | 'instituicao_nao_cadastrada';

/** E170.F2: o laudo assinado que não chega ao cliente (no eletrônico, só a ICP-Brasil é entregue). */
export type MotivoDaEntrega = 'assinatura_sem_certificado';

/** E180.F3 (GUS-254 item 1): o processamento parou por uma falha técnica; a retomada é sozinha. */
export type MotivoDaAnalise = 'processamento_falhou';

/** E180.F3.C02: onde a rodada parou e a causa sanitizada (a família do erro). */
export type EtapaDaFalha = 'processamento' | 'saidas' | 'leituras';
/** E200.F1 (GUS-220): onde a preparação está agora; `aguardando_vaga` é pedida e ainda sem rodada. */
export type EtapaDaPreparacao = 'aguardando_vaga' | EtapaDaFalha;
export type CausaDaFalha = 'registros' | 'arquivo' | 'tempo_esgotado' | 'inesperada';

export interface FalhaDoProcessamentoData {
  etapa: EtapaDaFalha;
  causa: CausaDaFalha;
}

export type MotivoDaPreparacao =
  | MotivoTecnico
  | MotivoDeEsperaProprio
  | MotivoDaLiberacao
  | MotivoDaEntrega
  | MotivoDaAnalise
  | MotivoDaProntidao
  | 'versao_nova';

export interface LinhaDoPacoteData {
  chave: ChaveDaLinha;
  estado: EstadoDaLinha;
  motivo?: MotivoDaPreparacao | null;
  proximo_passo?: ProximoPasso | null;
  recebidos?: number | null;
  /** E216.F3.C03 (GUS-317 4b): as linhas da seção 2 da prévia do exame que vale. */
  examinados?: number | null;
  artefato_id?: string | null;
  tentativas: number;
  retomavel: boolean;
  /** E169.F2: a saída pronta é a final — o exame assinado, o risco liberado ao cliente. */
  final: boolean;
  /** E170.F2: no exame e no risco com o arquivo guardado, o ponto da jornada. */
  jornada?: JornadaDaSaida | null;
  /** E170.F2: na saída bloqueada, os critérios da conferência por regras que não passaram. */
  reprovados?: ChaveDaConferencia[];
  /** E180.F3.C02: na análise que falhou, a etapa em que a rodada parou e a causa. */
  falha?: FalhaDoProcessamentoData | null;
  /** E184.F1: na prévia, as edições salvas numa análise anterior que seguem nesta versão. */
  edicoes_herdadas?: number;
  /** E184.F4: na saída bloqueada, a regra e o trecho de cada apontamento, para abrir no editor. */
  trechos_reprovados?: TrechoReprovadoData[];
}

export interface TrechoReprovadoData {
  chave: ChaveDaConferencia;
  /** Vazio quando o defeito é a falta: não há trecho a abrir. */
  trecho: string;
  no_documento: string;
}

export interface PacoteDoCasoData {
  case_id: string;
  avaliacao_id: string | null;
  versao: number | null;
  modo: ModoDoPacote | null;
  completo: boolean;
  preparando: boolean;
  linhas: LinhaDoPacoteData[];
  /** E169.F2: o laudo desta versão está assinado; completo, o pacote é a entrega ao cliente. */
  assinado: boolean;
  /** E170.F2: o cliente recebe o pacote (assinado, completo e com a assinatura que chega a ele). */
  entregue: boolean;
  /** E180.F3 (GUS-254 item 2): o caso não tem banco; as prévias saem e só a emissão final espera. */
  banco_pendente?: boolean;
  /** E200.F1 (GUS-220): a etapa da preparação em curso, com `preparando`; ausente sem preparação. */
  etapa?: EtapaDaPreparacao | null;
}

export interface RetomadaDoPacoteData {
  saida: SaidaDoPacote;
  justificativa: string;
}

export const JUSTIFICATIVA_MINIMA = 15;
export const JUSTIFICATIVA_MAXIMA = 500;

/** E172.F19: `atualizando` — a prévia está sendo refeita com a análise nova (só no download). */
export type MotivoDaFalhaDoPacote =
  | 'sem_permissao'
  | 'nao_encontrado'
  | 'indisponivel'
  | 'atualizando'
  | 'arquivos';

/** E172.F19: o código do download da prévia que a preparação está renovando. */
export const PREVIA_EM_ATUALIZACAO = 'PREVIA_EM_ATUALIZACAO';

export class PacoteDoCasoError extends Error {
  constructor(readonly motivo: MotivoDaFalhaDoPacote) {
    super(motivo);
    this.name = 'PacoteDoCasoError';
  }
}

/** Por que a retomada não foi aceita, pelo código do servidor. */
export type RecusaDaRetomada = 'em_andamento' | 'nao_retomavel' | 'justificativa' | 'indisponivel';

export class RetomadaDoPacoteError extends Error {
  constructor(readonly recusa: RecusaDaRetomada) {
    super(recusa);
    this.name = 'RetomadaDoPacoteError';
  }
}

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_CONFLICT = 409;
const HTTP_UNPROCESSABLE = 422;

function falhaHttp(err: unknown): { status?: number | undefined; detalhe?: unknown } {
  if (!axios.isAxiosError(err)) return {};
  const detalhe = (err.response?.data as { detail?: unknown } | undefined)?.detail;
  return { status: err.response?.status, detalhe };
}

function motivoDaFalha(err: unknown): MotivoDaFalhaDoPacote {
  const { status } = falhaHttp(err);
  if (status === HTTP_FORBIDDEN) return 'sem_permissao';
  if (status === HTTP_NOT_FOUND) return 'nao_encontrado';
  return 'indisponivel';
}

export async function lerPacoteDoCaso(
  client: AxiosInstance,
  caseId: string,
): Promise<PacoteDoCasoData> {
  try {
    const { data } = await client.get<PacoteDoCasoData>(`/casos/${caseId}/pacote`);
    return data;
  } catch (err) {
    throw new PacoteDoCasoError(motivoDaFalha(err));
  }
}

export async function retomarSaidaDoPacote(
  client: AxiosInstance,
  caseId: string,
  retomada: RetomadaDoPacoteData,
): Promise<PacoteDoCasoData> {
  try {
    const { data } = await client.post<PacoteDoCasoData>(
      `/casos/${caseId}/pacote/retomadas`,
      retomada,
    );
    return data;
  } catch (err) {
    const { status, detalhe } = falhaHttp(err);
    if (status === HTTP_CONFLICT && detalhe === 'PREPARACAO_EM_ANDAMENTO') {
      throw new RetomadaDoPacoteError('em_andamento');
    }
    if (status === HTTP_CONFLICT && detalhe === 'SAIDA_NAO_RETOMAVEL') {
      throw new RetomadaDoPacoteError('nao_retomavel');
    }
    if (status === HTTP_UNPROCESSABLE && typeof detalhe !== 'string') {
      throw new RetomadaDoPacoteError('justificativa');
    }
    throw new RetomadaDoPacoteError('indisponivel');
  }
}

/** E196.F1 (GUS-260): o pacote não sai com um arquivo recebido que não confere ou não está guardado. */
const ARQUIVOS_QUE_TRAVAM_O_PACOTE: ReadonlySet<unknown> = new Set([
  'ORIGINAL_DIVERGENTE',
  'ARQUIVO_DO_PACOTE_AUSENTE',
  // E216.F5.C03: o exame antigo sem a lista da seção 2, que o texto do PDF também não dá
  'SECAO_2_NAO_LOCALIZADA',
]);

/** O motivo da recusa do download, com o corpo de erro que chega como `Blob` relido. */
async function motivoDoDownload(err: unknown): Promise<MotivoDaFalhaDoPacote> {
  const { status, detalhe } = falhaHttp(await rehydrateBlobError(err));
  if (status === HTTP_CONFLICT && detalhe === PREVIA_EM_ATUALIZACAO) return 'atualizando';
  if (status === HTTP_CONFLICT && ARQUIVOS_QUE_TRAVAM_O_PACOTE.has(detalhe)) return 'arquivos';
  return motivoDaFalha(err);
}

/**
 * A prévia guardada, com o nome que o servidor manda e o código dos bytes entregues. E172.F19: com
 * a preparação renovando o pacote, a recusa `atualizando` (a prévia anterior não é servida).
 */
export async function baixarPreviaDoPacote(
  client: AxiosInstance,
  caseId: string,
  saida: SaidaDoPacote,
): Promise<ArquivoBaixado> {
  try {
    const resposta = await client.get<Blob>(`/casos/${caseId}/pacote/${saida}.pdf`, {
      responseType: 'blob',
    });
    return {
      blob: resposta.data,
      filename: nomeDoAnexo(resposta.headers['content-disposition'], `previa-${saida}.pdf`),
      codigoVerificacao: codigoDeVerificacao(resposta.headers),
    };
  } catch (err) {
    throw new PacoteDoCasoError(await motivoDoDownload(err));
  }
}

/**
 * E196.F1 (GUS-260): o exame (a prévia que vale) e os arquivos recebidos no caso num ZIP, com o
 * manifesto de cada recebimento. As recusas são as da prévia e `arquivos`.
 */
export async function baixarExameEArquivos(
  client: AxiosInstance,
  caseId: string,
): Promise<ArquivoBaixado> {
  try {
    const resposta = await client.get<Blob>(`/casos/${caseId}/pacote/exame-e-arquivos.zip`, {
      responseType: 'blob',
    });
    return {
      blob: resposta.data,
      filename: nomeDoAnexo(resposta.headers['content-disposition'], 'exame-e-arquivos.zip'),
      codigoVerificacao: codigoDeVerificacao(resposta.headers),
    };
  } catch (err) {
    throw new PacoteDoCasoError(await motivoDoDownload(err));
  }
}

/** E172.F19: a saída que a preparação está refazendo agora — o botão espera, "atualizando". */
export function emAtualizacao(linha: LinhaDoPacoteData): boolean {
  return (linha.chave === 'exame' || linha.chave === 'riscos') && linha.estado === 'em_andamento';
}

/** Enquanto o sistema prepara, a tela relê; parado, só a ação de alguém muda o card. */
/** E200.F2 (GUS-220, ADR-0143 §1): as etapas do caso que o card mostra em progressão. */
export type ChaveDaEtapa =
  | 'documentos'
  | 'leitura'
  | 'analise'
  | 'previas'
  | 'revisao'
  | 'assinatura'
  | 'entrega';
export type SituacaoDaEtapa = 'concluida' | 'atual' | 'pendente' | 'falhou';
export interface EtapaDoPacote {
  chave: ChaveDaEtapa;
  situacao: SituacaoDaEtapa;
}

const ORDEM_DAS_ETAPAS: readonly ChaveDaEtapa[] = [
  'documentos',
  'leitura',
  'analise',
  'previas',
  'revisao',
  'assinatura',
  'entrega',
];

const REVISADAS: ReadonlySet<JornadaDaSaida> = new Set(['revisado', 'assinado', 'entregue']);

/**
 * A progressão do caso a partir do pacote, sem inventar: cada etapa é concluída pelo que o servidor
 * diz (documentos prontos, análise pronta, prévias guardadas, saídas revisadas, laudo assinado,
 * entregue); a primeira não concluída é a atual — ou falhou, quando a análise ou uma prévia falhou
 * —; as seguintes ficam a fazer. A leitura termina quando nenhuma linha espera por ela (em curso ou
 * em conferência) e a rodada não está nas leituras; as outras saem das linhas. Uma etapa concluída
 * conclui as anteriores. Só as saídas aplicáveis contam (em JUÍZO não há risco).
 */
export function etapasDoPacote(pacote: PacoteDoCasoData): EtapaDoPacote[] {
  const documentos = pacote.linhas.find((l) => l.chave === 'documentos');
  const analise = pacote.linhas.find((l) => l.chave === 'analise');
  const saidas = pacote.linhas.filter(
    (l) => (l.chave === 'exame' || l.chave === 'riscos') && l.estado !== 'nao_se_aplica',
  );
  const lendo =
    pacote.etapa === 'leituras' ||
    pacote.linhas.some(
      (l) => l.motivo === 'leitura_em_andamento' || l.motivo === 'leitura_em_conferencia',
    );
  const documentosProntos = documentos?.estado === 'pronto';
  const guardadas = saidas.length > 0 && saidas.every((l) => l.jornada != null);
  const concluida: Record<ChaveDaEtapa, boolean> = {
    documentos: documentosProntos,
    leitura: documentosProntos && !lendo,
    analise: analise?.estado === 'pronto',
    previas: guardadas,
    revisao: guardadas && saidas.every((l) => l.jornada != null && REVISADAS.has(l.jornada)),
    assinatura: pacote.assinado,
    entrega: pacote.entregue,
  };
  // A etapa concluída conclui as anteriores: o laudo assinado passou pelas prévias e pela revisão,
  // mesmo que o risco, depois, espere a liberação ou falhe.
  let depois = false;
  for (const chave of [...ORDEM_DAS_ETAPAS].reverse()) {
    if (depois) concluida[chave] = true;
    else if (concluida[chave]) depois = true;
  }
  const falhou: Partial<Record<ChaveDaEtapa, boolean>> = {
    analise: analise?.estado === 'falhou',
    previas: saidas.some((l) => l.estado === 'falhou'),
  };
  let atualVista = false;
  return ORDEM_DAS_ETAPAS.map((chave) => {
    if (concluida[chave]) return { chave, situacao: 'concluida' };
    if (atualVista) return { chave, situacao: 'pendente' };
    atualVista = true;
    return { chave, situacao: falhou[chave] === true ? 'falhou' : 'atual' };
  });
}

export function precisaReler(pacote: PacoteDoCasoData | null): boolean {
  if (pacote === null) return false;
  return pacote.preparando || pacote.linhas.some((l) => l.estado === 'em_andamento');
}

/** A justificativa da retomada, com as bordas que o servidor tira. */
export function justificativaValida(texto: string): boolean {
  const limpo = texto.trim();
  return limpo.length >= JUSTIFICATIVA_MINIMA && limpo.length <= JUSTIFICATIVA_MAXIMA;
}
